<template>
  <div class="page">
    <main class="content">
      <section class="not-found-shell" role="status" aria-live="polite">
        <p class="kicker">You Lost?</p>
        <h1 class="not-found-code">404</h1>
        <h2 class="not-found-title">The party is that way!</h2>
        <p class="not-found-description">
          We could not find the page you requested. The link may be outdated,
          or the page no longer exists.
        </p>

        <p class="not-found-path">{{ currentPath }}</p>

        <div class="not-found-actions">
          <router-link to="/" class="discord-button">Back to Home</router-link>
          <router-link to="/guides" class="secondary-action">Browse Guides</router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: 'NotFoundPage',
  computed: {
    currentPath() {
      return `Missing route: ${this.$route.fullPath}`;
    }
  }
};
</script>

<style scoped>
.not-found-shell {
  width: 100%;
  max-width: 780px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  isolation: isolate;
  overflow: hidden;
  padding: clamp(1.4rem, 3vw, 2.2rem);
  border-radius: var(--border-radius-xl);
  border: 1px solid rgba(255, 255, 255, 0.16);
  background:
    linear-gradient(160deg, rgba(255, 255, 255, 0.09) 0%, rgba(255, 255, 255, 0.01) 35%, rgba(255, 255, 255, 0) 100%),
    var(--color-bg-secondary);
  box-shadow:
    var(--shadow-xl),
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    inset 0 -30px 80px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(4px);
  animation: floatIn 0.45s ease-out both;
}

.not-found-shell::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(125deg, rgba(0, 183, 255, 0.45), rgba(254, 94, 161, 0.35), rgba(255, 255, 255, 0.12));
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 2;
}

.not-found-shell::after {
  content: "";
  position: absolute;
  width: 320px;
  height: 320px;
  right: -140px;
  top: -170px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 183, 255, 0.22) 0%, rgba(0, 183, 255, 0) 70%);
  pointer-events: none;
  z-index: 0;
}

.kicker {
  margin: auto;
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.not-found-code {
  margin: 0.35rem 0 0;
  font-size: clamp(3rem, 11vw, 6.4rem);
  line-height: 1;
  font-weight: 800;
  background: var(--color-gradient-primary);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 12px 30px rgba(254, 94, 161, 0.18);
}

.not-found-title {
  margin: 0.7rem 0 0;
  font-size: clamp(1.2rem, 3.2vw, 2rem);
  line-height: 1.25;
}

.not-found-description {
  margin: 0.8rem auto 0;
  max-width: 56ch;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
}

.not-found-path {
  margin: 1rem auto 0;
  max-width: 100%;
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  word-break: break-all;
  padding: 0.65rem 0.75rem;
  border-radius: var(--border-radius-sm);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.not-found-shell > * {
  position: relative;
  z-index: 1;
}

.not-found-actions {
  margin-top: 1.25rem;
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
}

.secondary-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-md) var(--spacing-lg);
  font-size: var(--font-size-md);
  font-weight: 600;
  border-radius: var(--border-radius-lg);
  text-decoration: none;
  color: var(--color-text-primary);
  background: var(--color-bg-tertiary);
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: all var(--transition-normal);
}

.secondary-action:hover {
  transform: translateY(-2px);
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: var(--shadow-md);
}

.secondary-action:focus-visible,
.discord-button:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}

@keyframes floatIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 640px) {
  .not-found-actions {
    flex-direction: column;
    align-items: center;
  }

  .not-found-actions .discord-button,
  .secondary-action {
    width: min(320px, 100%);
  }
}
</style>
